import { z } from 'zod'

import { BookingDTOState, BookingDTOTransportMode } from '@/clients/booking'

import { HouseCargoSchema } from './houseShipment.ts'
import { SHIPMENT_STATES } from '../domain/shipment.ts'
import { TimelineSchema } from '../utils/getTimelineData.ts'
import { IContainerTP } from '../utils/transportPlanContainerPlans.ts'
import { TRANSPORT_MODES, TRANSPORT_TYPES } from '..'

export const OverviewItemSchema = z.discriminatedUnion('type', [
  z.object({
    id: z.string(),
    type: z.literal('shipment'),
    transportMode: z.enum(TRANSPORT_MODES),
    transportType: z.enum(TRANSPORT_TYPES),
    containerQuantity: z.number(),
    state: z.enum(SHIPMENT_STATES),
    delayDays: z.number(),
    bookedDate: z.coerce
      .date()
      .nullish()
      .transform((data) => data ?? undefined),
    createdDate: z.coerce
      .date()
      .nullish()
      .transform((data) => data ?? undefined),
    deliveryDate: z.coerce
      .date()
      .nullish()
      .transform((data) => data ?? undefined),
    pickupDate: z.coerce
      .date()
      .nullish()
      .transform((data) => data ?? undefined),
    customerReference: z.string().default('—'),
    shipper: z.string().default('—'),
    timeline: TimelineSchema,
    cargo: z
      .array(
        z.object({
          volume: z
            .number()
            .nullish()
            .transform((data) => data ?? undefined),
          grossWeight: z
            .number()
            .nullish()
            .transform((data) => data ?? undefined),
          quantity: z.number(),
          width: z
            .number()
            .nullish()
            .transform((data) => data ?? undefined),
          length: z
            .number()
            .nullish()
            .transform((data) => data ?? undefined),
          type: z.string(),
          commodityType: z.string(),
          height: z
            .number()
            .nullish()
            .transform((data) => data ?? undefined),
          containerType: z
            .string()
            .nullish()
            .transform((data) => data ?? undefined),
        })
      )
      .default([]),
    // Leaving this as any and overriding the inferred type, since it's a very complex type and TP is going to be phased
    // out with house shipments, so instead of this if we have a more specific frontend app model, it'll make it a
    // lot easier to migrate
    containerPlans: z.array(z.any()).default([]),
    houseCargos: z.array(HouseCargoSchema).default([]),
  }),
  z.object({
    id: z.string(),
    type: z.literal('booking'),
    transportMode: z.nativeEnum(BookingDTOTransportMode),
    transportType: z.enum(TRANSPORT_TYPES),
    containerQuantity: z.number(),
    state: z.nativeEnum(BookingDTOState),
    bookedDate: z.coerce
      .date()
      .nullish()
      .transform((data) => data ?? undefined),
    createdDate: z.coerce
      .date()
      .nullish()
      .transform((data) => data ?? undefined),
    deliveryDate: z.coerce
      .date()
      .nullish()
      .transform((data) => data ?? undefined),
    pickupDate: z.coerce
      .date()
      .nullish()
      .transform((data) => data ?? undefined),
    customerReference: z.string().default('—'),
    shipper: z.string().default('—'),
    timeline: TimelineSchema,
    cargo: z
      .array(
        z.object({
          type: z.string(),
          commodityID: z
            .string()
            .nullish()
            .transform((data) => data ?? undefined),
          commodity: z
            .string()
            .nullish()
            .transform((data) => data ?? undefined),
          commodityDetails: z
            .string()
            .nullish()
            .transform((data) => data ?? undefined),
          quantity: z
            .number()
            .nullish()
            .transform((data) => data ?? undefined),
          grossWeightKg: z
            .number()
            .nullish()
            .transform((data) => data ?? undefined),
          dimensions: z
            .object({
              heightCm: z.number(),
              lengthCm: z.number(),
              widthCm: z.number(),
            })
            .nullish()
            .transform((data) => data ?? undefined),
          containerSpecification: z
            .object({
              temperatureC: z
                .number()
                .nullish()
                .transform((data) => data ?? undefined),
              humidityPercent: z
                .number()
                .nullish()
                .transform((data) => data ?? undefined),
            })
            .nullish()
            .transform((data) => data ?? undefined),
        })
      )
      .default([]),
    containerPlans: z.array(z.any()).default([]),
    houseCargos: z.array(HouseCargoSchema).default([]),
  }),
])

export type OverviewItem = Omit<z.infer<typeof OverviewItemSchema>, 'containerPlans'> & {
  containerPlans: IContainerTP[]
  delayDays?: number
}

export type OverviewItemKey = keyof OverviewItem

export type OverviewItemResponse = {
  items: OverviewItem[]
  total: number
}
